import React, { FC, useState } from 'react';
import styled from 'styled-components';
import OvListTable from '../atoms/OvListTable';
import OvListTableRow from '../atoms/OvListTableRow';
import OvListTableHeaderField from '../atoms/OvListTableHeaderField';
import { useTranslation } from 'react-i18next';
import { Scan } from '../../../common/model/dto/scan';
import OvListTableField from '../atoms/OvListTableField';
import OvDateParser from '../atoms/OvDateParser';
import { Link } from 'react-router-dom';
import OvButton from '../atoms/OvButton';
import Variables from '../../../design-system/variables';
import Colours from '../../../design-system/colours';
import OvNoContent from '../molecules/OvNoContent';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, Tooltip } from '@mui/material';
import OvConfirmationDialog from '../molecules/OvConfirmationDialog';
import { useAdminScanMutations } from '../../../hooks/use-admin-scan-mutations';
import OvCreateScanDialog from '../molecules/OvCreateScanDialog';
import { CreateScanRequest } from '../../../common/model/dto/create-scan-request';
import OvLoadingIndicator from '../atoms/OvLoadingIndicator';
import OvErrorMessage from '../atoms/OvErrorMessage';
import useAuthorized from '../../../hooks/use-authorized';
import { Resource } from '../../../common/model/type/resource.enum';
import DateUtils from '../../../common/utils/services/date-utils';
import { ScansService } from '../../../services/scans.service';
import { AxiosError } from 'axios';

const OvUserScanList: FC<{
  scans: Scan[];
  showDetailsAction?: boolean;
  showOperation?: boolean;
  selectedUserId?: string;
  selectedDay?: string;
  refetch?: () => {};
}> = ({
  scans,
  showDetailsAction,
  showOperation,
  selectedUserId,
  selectedDay,
  refetch,
}) => {
  const [isCreateScanDialogOpen, setIsCreateScanDialogOpen] =
    useState<boolean>(false);
  const { deleteScan } = useAdminScanMutations();
  const [scanErrorMessage, setScanErrorMessage] = useState<string>('');
  const [isScanLoading, setIsScanLoading] = useState<boolean>(false);

  const applicationOperations = useAuthorized(Resource.Application);
  const scanOperations = useAuthorized(Resource.Scan);

  const scanReadAllowed =
    scanOperations.read || applicationOperations.supervise;
  const scanCreateAllowed =
    scanOperations.create || applicationOperations.supervise;
  const scanDeleteAllowed =
    scanOperations.delete || applicationOperations.supervise;

  const headerNames = [
    'common.day',
    'common.dailyData.lh',
    'common.dailyData.pg',
    'common.dailyData.e3g',
    'common.createdAt',
    'common.modifiedAt',
  ];

  if (showDetailsAction) {
    headerNames.push('common.action');
  }

  if (showOperation) {
    headerNames.push('common.operation');
  }

  const { t } = useTranslation();
  const [isOpenDeleteScanDialog, setOpenDeleteScanDialog] =
    useState<boolean>(false);
  const [deletableScanId, setDeletableScanId] = useState<string>();

  const openDeleteScanDialog = (id?: string) => {
    setDeletableScanId(id);
    setOpenDeleteScanDialog(true);
  };

  const onDelete = () => {
    if (!deletableScanId) {
      return;
    }

    deleteScan.mutate(deletableScanId, {
      onSettled: () => {
        setOpenDeleteScanDialog(false);
        refetch?.();
      },
    });
  };

  const onSaveScan = async (request: CreateScanRequest) => {
    try {
      setIsScanLoading(true);

      await ScansService.createScan(request);
    } catch (e: any) {
      setScanErrorMessage(((e as AxiosError)?.response?.data as any)?.message);
      setIsScanLoading(false);
      return;
    } finally {
      setIsScanLoading(false);
    }

    setIsCreateScanDialogOpen(false);
    await refetch?.();
  };

  return (
    <Container>
      {selectedUserId && scanCreateAllowed && (
        <StyledCreateScanButton
          onClick={() => {
            setScanErrorMessage('');
            setIsCreateScanDialogOpen(true);
          }}
        >
          {t('scans.createScan')}
        </StyledCreateScanButton>
      )}

      {deleteScan.error && (
        <OvErrorMessage message={deleteScan.error.message} />
      )}

      <WrapperBox>
        {scans?.length ? (
          <OvListTable>
            <OvListTableRow>
              {headerNames.map((headerName) => (
                <OvListTableHeaderField key={headerName}>
                  {t(headerName)}
                </OvListTableHeaderField>
              ))}
            </OvListTableRow>
            {scans.map((scan, index) => (
              <OvListTableRow key={index}>
                <OvListTableField>{scan?.day}</OvListTableField>
                <OvListTableField>
                  {scan?.displayLh?.value ? scan.displayLh.value : '-'}
                </OvListTableField>
                <OvListTableField>
                  {scan?.displayPg?.value ? scan.displayPg.value : '-'}
                </OvListTableField>
                <OvListTableField>
                  {scan?.displayLrE3g?.value ? scan.displayLrE3g.value : '-'}
                </OvListTableField>
                <OvListTableField>
                  <OvDateParser
                    date={scan?.created_at as Date}
                    format={'YYYY.MM.DD - hh:mm A'}
                  />
                </OvListTableField>
                <OvListTableField>
                  <OvDateParser
                    date={scan?.updated_at as Date}
                    format={'YYYY.MM.DD - hh:mm A'}
                  />
                </OvListTableField>
                {showOperation && scanDeleteAllowed && (
                  <OvListTableField>
                    <Tooltip title={t('common.dailyData.deleteScan') || ''}>
                      <StyledIconButton
                        disabled={scan.is_daily_log_record}
                        onClick={() => openDeleteScanDialog(scan.id)}
                        className="actionIcon"
                      >
                        <DeleteIcon />
                      </StyledIconButton>
                    </Tooltip>
                  </OvListTableField>
                )}
                {showDetailsAction && scanReadAllowed && (
                  <OvListTableField>
                    <StyledOvButton>
                      <Link to={`/scan-list/${scan.id}`}>
                        {t('common.actions.details')}
                      </Link>
                    </StyledOvButton>
                  </OvListTableField>
                )}
              </OvListTableRow>
            ))}
          </OvListTable>
        ) : (
          <OvNoContent>
            {t('common.userSearchFilters.emptyUserList')}
          </OvNoContent>
        )}
      </WrapperBox>
      <OvConfirmationDialog
        icon={<StyledDeleteIcon />}
        isOpen={isOpenDeleteScanDialog}
        onCancel={() => setOpenDeleteScanDialog(false)}
        onConfirm={onDelete}
        description="common.dailyData.permanentScanDelete"
        title="common.areYouSure"
      />

      {selectedUserId && (
        <OvCreateScanDialog
          isOpen={isCreateScanDialogOpen}
          isScanLoading={isScanLoading}
          onCancel={() => setIsCreateScanDialogOpen(false)}
          onSave={onSaveScan}
          selectedUserId={selectedUserId}
          selectedDay={selectedDay || DateUtils.getDbDateTag(new Date())}
          showDaySelector={true}
          errorMessage={scanErrorMessage}
        />
      )}
      {deleteScan.isPending && <OvLoadingIndicator position="fixed" />}
    </Container>
  );
};

export default OvUserScanList;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const WrapperBox = styled.div`
  padding: 1.5rem 1rem 1rem;
  overflow-x: auto;
`;

const StyledOvButton = styled(OvButton)`
  && {
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    padding: 0;
    margin: 0;
    background-color: ${Colours.OV_BASE};

    &:hover {
      a {
        color: ${Colours.OV_BASE};
      }
    }

    a {
      border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
      padding: 0.25rem 1rem;
      text-decoration: none;
      text-transform: none;
      font-weight: bold;
      font-size: 0.75rem;
      color: ${Colours.OV_WHITE};
    }
  }
`;

const StyledIconButton = styled(IconButton)`
  && {
    color: ${Colours.OV_BASE};
    height: 2rem;
    width: 2rem;

    .MuiSvgIcon-root {
      width: 1.5rem;
      height: 1.5rem;
    }

    &.MuiButtonBase-root {
      padding: 0.375rem;
      border-radius: 50%;
    }
  }
`;

const StyledDeleteIcon = styled(DeleteIcon)`
  && {
    box-sizing: content-box;
    padding: 0.5rem;
    background: ${Colours.OV_BASE};
    border-radius: 50%;
    width: 2rem;
    height: 2rem;
    text-align: center;
    display: block;
    margin: 1rem auto 0 auto;
    color: ${Colours.OV_SEMI_LIGHT};
  }
`;

const StyledCreateScanButton = styled(OvButton)`
  && {
    border-radius: ${Variables.borderRadius.XLARGE};
    padding: 0 1rem;
    text-transform: none;
    justify-self: center;
    align-self: flex-end;
  }
`;
